import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getUser, getClient } from '../services/api';
import { Client, User } from '../types';

export const useClientData = (clientId?: string) => {
  const [user, setUser] = useState<User | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  // Add a timestamp to prevent caching
  const [lastFetchTime, setLastFetchTime] = useState(0);

  const fetchData = useCallback(async (forceRefresh = false) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const userData = await getUser(token);
      setUser(userData);

      if (clientId && !isNaN(parseInt(clientId))) {
        // Create the URL with cache busting parameter
        let url = clientId;
        if (forceRefresh) {
          url = `${clientId}?_t=${Date.now()}`;
        }
        
        const clientData = await getClient(url, token);
        setClient(clientData);
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      
      // Don't log out on 500 errors, only on auth errors
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        authContext?.setUser(null);
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  }, [clientId, authContext, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { 
    user, 
    client, 
    isLoading,
    refreshClient: () => fetchData(true)
  };
};